import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../css/ClockPicker.css';

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

/* 24h → 12h + period */
const to12 = (h24) => {
    const period = h24 >= 12 ? 'PM' : 'AM';
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    return { h12, period };
};

/* 12h + period → 24h */
const to24 = (h12, period) => {
    if (period === 'AM') return h12 === 12 ? 0 : h12;
    return h12 === 12 ? 12 : h12 + 12;
};

const pad = (n) => String(n).padStart(2, '0');

const ClockPicker = ({ value, onChange, onClose }) => {
    const { t } = useTranslation();
    const [h24, m] = (value || '09:00').split(':').map(Number);
    const init = to12(h24);

    const [hour12, setHour12] = useState(init.h12);
    const [minute, setMinute] = useState(m);
    const [period, setPeriod] = useState(init.period);
    const [mode, setMode] = useState('hour');
    const faceRef = useRef(null);

    const getAngleFromEvent = useCallback((e) => {
        const rect = faceRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const angle = Math.atan2(clientX - cx, -(clientY - cy)) * (180 / Math.PI);
        return (angle + 360) % 360;
    }, []);

    const handleFaceClick = useCallback((e) => {
        const angle = getAngleFromEvent(e);
        if (mode === 'hour') {
            let h = Math.round(angle / 30) % 12;
            setHour12(h === 0 ? 12 : h);
            setTimeout(() => setMode('minute'), 200);
        } else {
            let m = Math.round(angle / 6) % 60;
            setMinute(m);
        }
    }, [mode, getAngleFromEvent]);

    const handleConfirm = () => {
        const h24 = to24(hour12, period);
        onChange(`${pad(h24)}:${pad(minute)}`);
        onClose();
    };

    const handAngle = mode === 'hour'
        ? (hour12 % 12) * 30
        : minute * 6;

    const numbers = mode === 'hour' ? HOURS_12 : MINUTES;
    const activeValue = mode === 'hour' ? hour12 % 12 : minute;

    // Display in 12h format
    const displayH24 = to24(hour12, period);

    return (
        <div className="clock-overlay" onClick={onClose}>
            <div className="clock-popup" onClick={e => e.stopPropagation()}>
                {/* Display */}
                <div className="clock-display">
                    <div className="clock-display-time">
                        <button
                            className={`clock-display-part ${mode === 'hour' ? 'active' : ''}`}
                            onClick={() => setMode('hour')}
                        >
                            {hour12}
                        </button>
                        <span className="clock-display-sep">:</span>
                        <button
                            className={`clock-display-part ${mode === 'minute' ? 'active' : ''}`}
                            onClick={() => setMode('minute')}
                        >
                            {pad(minute)}
                        </button>
                    </div>
                    <div className="clock-ampm">
                        <button
                            className={`clock-ampm-btn ${period === 'AM' ? 'active' : ''}`}
                            onClick={() => setPeriod('AM')}
                        >
                            {t('recurring.clock.am')}
                        </button>
                        <button
                            className={`clock-ampm-btn ${period === 'PM' ? 'active' : ''}`}
                            onClick={() => setPeriod('PM')}
                        >
                            {t('recurring.clock.pm')}
                        </button>
                    </div>
                </div>

                {/* 24h preview */}
                <div className="clock-24h-preview">
                    {pad(displayH24)}:{pad(minute)}
                </div>

                {/* Clock face */}
                <div className="clock-face" ref={faceRef} onClick={handleFaceClick}>
                    <div className="clock-center" />
                    <div
                        className="clock-hand"
                        style={{ transform: `rotate(${handAngle}deg)` }}
                    >
                        <div className="clock-hand-tip" />
                    </div>
                    {numbers.map((n, i) => {
                        const angle = (i * (360 / numbers.length)) - 90;
                        const rad = angle * (Math.PI / 180);
                        const r = 38;
                        const x = 50 + r * Math.cos(rad);
                        const y = 50 + r * Math.sin(rad);
                        const isActive = mode === 'hour'
                            ? (n % 12) === activeValue
                            : n === activeValue;
                        return (
                            <div
                                key={n}
                                className={`clock-number ${isActive ? 'active' : ''}`}
                                style={{ left: `${x}%`, top: `${y}%` }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (mode === 'hour') {
                                        setHour12(n === 0 ? 12 : n);
                                        setTimeout(() => setMode('minute'), 200);
                                    } else {
                                        setMinute(n);
                                    }
                                }}
                            >
                                {mode === 'minute' ? pad(n) : n}
                            </div>
                        );
                    })}
                </div>

                {/* Actions */}
                <div className="clock-actions">
                    <button className="clock-btn clock-btn-cancel" onClick={onClose}>
                        {t('recurring.cancel')}
                    </button>
                    <button className="clock-btn clock-btn-ok" onClick={handleConfirm}>
                        {t('recurring.clock.confirm')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClockPicker;
